import { confirmDialog } from "primereact/confirmdialog";
import { enqueueSnackbar } from "notistack";
import {
  createAstInstitution,
  deleteAstInstitution,
  getAstInstitution,
  getAstInstitutionCategories,
  getAstInstitutions,
  searchAstInstitutions,
  updateAstInstitution,
  getAllWards,
} from "../../services/ValitanApiService";

const parsePage = (response) => {
  const field = response?.data?.data ?? response?.data ?? {};
  const content = Array.isArray(field) ? field : field.content || [];
  return {
    items: content,
    paginationInfo: {
      totalElements: field.total_elements ?? field.totalElements ?? content.length,
      totalPages: field.total_pages ?? field.totalPages ?? 1,
      pageNumber: field.number ?? field.page_number ?? 0,
      pageSize: field.size ?? field.page_size ?? content.length,
      sort: field.sort || {},
    },
  };
};

export const getInstitutions = async (page = 0, size = 10) => {
  try {
    const response = await getAstInstitutions(page, size);
    if (response?.data?.status === "failed") {
      enqueueSnackbar(response.data.message || "Failed to fetch institutions.", {
        variant: "error",
      });
      return { items: [], paginationInfo: {} };
    }
    return parsePage(response);
  } catch (error) {
    console.error("Error fetching institutions:", error);
    enqueueSnackbar("Error fetching institutions. Please try again.", {
      variant: "error",
    });
    return { items: [], paginationInfo: {} };
  }
};

export const searchInstitutionsByName = async (name) => {
  try {
    const response = await searchAstInstitutions(name);
    const data = response?.data?.data ?? response?.data ?? [];
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Error searching institutions:", error);
    enqueueSnackbar("Error searching institutions.", { variant: "error" });
    return [];
  }
};

export const getInstitutionById = async (id) => {
  const response = await getAstInstitution(id);
  return response?.data?.data ?? response?.data ?? null;
};

export const getInstitutionCategoryOptions = async () => {
  try {
    const response = await getAstInstitutionCategories(0, 1000);
    const { items } = parsePage(response);
    return items.map((c) => ({
      value: c.id,
      label: c.code ? `${c.name} (${c.code})` : c.name,
      code: c.code,
      name: c.name,
    }));
  } catch (error) {
    console.error("Error fetching institution categories:", error);
    enqueueSnackbar("Error fetching institution categories.", { variant: "error" });
    return [];
  }
};

export const getWardOptions = async () => {
  try {
    const response = await getAllWards(0, 2000);
    const field = response?.data?.data ?? response?.data ?? {};
    const content = Array.isArray(field) ? field : field.content || [];
    return content
      .map((w) => ({
        value: w.id,
        label: w.council?.name ? `${w.name} — ${w.council.name}` : w.name,
      }))
      .sort((a, b) => String(a.label).localeCompare(String(b.label)));
  } catch (error) {
    console.error("Error fetching wards:", error);
    enqueueSnackbar("Error fetching wards.", { variant: "error" });
    return [];
  }
};

const validateInstitutionForm = (form, setErrors) => {
  const errors = { name: "", category: "" };
  let valid = true;
  if (!String(form.name || "").trim()) {
    errors.name = "Institution name is required";
    valid = false;
  }
  setErrors(errors);
  return valid;
};

export const createOrUpdateInstitution = async ({
  form,
  editingId,
  loadInstitutions,
  toggleModal,
  setErrors,
}) => {
  if (!validateInstitutionForm(form, setErrors)) return false;

  const payload = {
    name: String(form.name).trim(),
    code: form.code ? String(form.code).trim() : undefined,
    description: form.description ? String(form.description).trim() : undefined,
    ward_id: form.wardId || undefined,
    ast_asset_institution_category_id: form.categoryId || undefined,
  };

  try {
    const response = editingId
      ? await updateAstInstitution(editingId, payload)
      : await createAstInstitution(payload);

    if (response?.data?.status === "success") {
      enqueueSnackbar(response.data.message || "Institution saved.", {
        variant: "success",
      });
      toggleModal();
      await loadInstitutions();
      return true;
    }
    enqueueSnackbar(response?.data?.message || "Failed to save institution.", {
      variant: "error",
    });
    return false;
  } catch (error) {
    const msg =
      error?.response?.data?.message ||
      error?.message ||
      "Error saving institution.";
    enqueueSnackbar(msg, { variant: "error" });
    console.error(error);
    return false;
  }
};

const deleteInstitution = async (id, loadInstitutions) => {
  try {
    const response = await deleteAstInstitution(id);
    if (response?.data?.status === "success") {
      enqueueSnackbar(response.data.message || "Institution deleted.", {
        variant: "success",
      });
      await loadInstitutions();
    } else {
      enqueueSnackbar(response?.data?.message || "Failed to delete institution.", {
        variant: "error",
      });
    }
  } catch (error) {
    const msg =
      error?.response?.data?.message ||
      "Failed to delete institution. It may be in use by assets.";
    enqueueSnackbar(msg, { variant: "error" });
    console.error(error);
  }
};

export const confirmDeleteInstitution = (id, loadInstitutions) => {
  confirmDialog({
    message:
      "Delete this institution? Assets linked to it may keep the free-text name but lose the institution_id link.",
    header: "Confirm delete",
    icon: "pi pi-exclamation-triangle",
    acceptClassName: "p-button-danger",
    accept: () => deleteInstitution(id, loadInstitutions),
  });
};
